import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  ArrowRightOnRectangleIcon,
  BuildingOffice2Icon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { ImpersonationBanner } from './platform/ImpersonationBanner';

interface PlatformLayoutProps {
  children: React.ReactNode;
}

function initials(name: string | undefined, email: string | undefined) {
  const n = (name || '').trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return n.slice(0, 2).toUpperCase();
  }
  const e = (email || '').split('@')[0] || '?';
  return e.slice(0, 2).toUpperCase();
}

const PlatformLayout: React.FC<PlatformLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-[#f1f4f9] flex flex-col">
      <ImpersonationBanner />
      <header className="relative border-b border-slate-800/40 bg-gradient-to-r from-[#0f172a] via-[#0f172a] to-[#0c1422] text-white shadow-lg shadow-slate-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-[4.25rem] flex-wrap items-center justify-between gap-3 py-3 sm:min-h-[4.25rem] sm:py-0">
            <div className="flex min-w-0 flex-1 basis-[min(100%,16rem)] items-center gap-4 sm:basis-auto">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20 shadow-inner">
                <ShieldCheckIcon className="h-6 w-6 text-sky-200" aria-hidden />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-base font-semibold tracking-tight text-white sm:text-lg">
                    Administración de plataforma
                  </h1>
                  <span className="hidden rounded-md bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-300 sm:inline">
                    Multi-tenant
                  </span>
                </div>
                <p className="mt-0.5 flex items-center gap-2 text-xs text-slate-400">
                  <BuildingOffice2Icon className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
                  <span className="truncate">Consola de gestión de funerarias cliente</span>
                </p>
              </div>
            </div>

            <nav className="flex flex-wrap items-center justify-end gap-1 rounded-xl border border-white/10 bg-white/5 p-1 sm:justify-start">
              <NavLink
                to="/platform"
                end
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? 'bg-white/15 text-white shadow-inner'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white'
                  }`
                }
              >
                <BuildingOffice2Icon className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                Funerarias
              </NavLink>
              <NavLink
                to="/platform/plans"
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? 'bg-white/15 text-white shadow-inner'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white'
                  }`
                }
              >
                <SparklesIcon className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                Planes
              </NavLink>
              <NavLink
                to="/platform/incidents"
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? 'bg-white/15 text-white shadow-inner'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white'
                  }`
                }
              >
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                Incidencias
              </NavLink>
            </nav>

            <div className="flex shrink-0 items-center gap-3">
              <div className="hidden min-w-0 items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 sm:flex">
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500/90 to-indigo-600 text-xs font-bold text-white shadow-md ring-2 ring-white/20"
                  aria-hidden
                >
                  {initials(user?.name, user?.email)}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-white">{user?.name || 'Usuario'}</p>
                  <p className="truncate text-xs text-slate-400">{user?.email}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => logout()}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-slate-100 transition-colors hover:border-white/25 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/80"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 text-slate-300" aria-hidden />
                <span className="hidden sm:inline">Cerrar sesión</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-8 sm:px-6 lg:px-8">{children}</main>
    </div>
  );
};

export default PlatformLayout;
